import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Topbar from '../../components/layout/public/Topbar';
import Footer from '../../components/layout/public/Footer';

const Home = () => {
    const [searchData, setSearchData] = useState({
        jobQuery: '',
        location: ''
    });
    const [isSearching, setIsSearching] = useState(false);
    const navigate = useNavigate();

    // Initialize animations
    useEffect(() => {
        const elements = document.querySelectorAll('.fade-in-up');
        elements.forEach(el => {
            el.style.transform = 'translateY(0)';
            el.style.opacity = '1';
        });
    }, []);

    // Handle tag button click
    const handleTagClick = (tagText) => {
        setSearchData(prev => ({
            ...prev,
            jobQuery: tagText
        }));
    };

    // Handle search
    const handleSearch = () => {
        const { jobQuery, location } = searchData;
        
        if (!jobQuery.trim()) {
            // Add subtle shake animation to job search
            const jobInput = document.getElementById('job-search');
            if (jobInput) {
                jobInput.classList.add('border', 'border-red-500');
                jobInput.focus();
                
                setTimeout(() => {
                    jobInput.classList.remove('border', 'border-red-500');
                }, 1000);
            }
            
            return;
        }
        
        // Show loading state
        setIsSearching(true);
        
        // Build query parameters
        const params = new URLSearchParams();
        params.append('q', jobQuery.trim());
        if (location.trim()) {
            params.append('location', location.trim());
        }
        
        // Simulate API call delay and then redirect
        setTimeout(() => {
            // Navigate to job listings with search parameters
            navigate(`/jobs?${params.toString()}`);
            setIsSearching(false);
        }, 500);
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div className="bg-[#222629] text-white">            
            {/* SEO Settings */}
            <title>{`${process.env.REACT_APP_NAME} | Find Jobs & Career Opportunities in India`}</title>
            <meta
                name="description"
                content="Hunar Pool connect talented professionals with career opportunities that truly fit their skills, goals, and aspirations. Our platform is designed with job seekers at the center, helping them discover roles where they can grow, contribute, and thrive."
            />

            {/* Hero Section */}
            <section className="min-h-screen pt-24 pb-16 flex items-center">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="max-w-10xl mx-auto">
                        {/* Main Heading & Subtext */}
                        <div className="text-center mb-10 md:mb-14">
                            <h1 className="text-2xl md:text-6xl font-bold text-white mb-5 md:mb-6 leading-tight transform -translate-y-4 fade-in-up">
                                Find your
                                <span className="gradient-text"> dream job </span>
                                with Hunar Pool
                            </h1>
                            <p className="text-sm md:text-lg text-gray-300 max-w-2xl mx-auto transform -translate-y-4 fade-in-up delay-1">
                                Showcase Your Skills. Get Noticed. Connect With Opportunities.
                            </p>
                            {/* <p className="text-lg text-gray-300 mx-auto mb-8 transform -translate-y-4 fade-in-up delay-1">
                                Hunar Pool helps talent build portfolios and find matched opportunities with verified companies.
                            </p> */}
                        </div>
                        
                        {/* Main Search Box */}
                        <div className="max-w-5xl mx-auto mb-10 md:mb-14 transform -translate-y-4 fade-in-up delay-2">
                            <div className="search-shadow bg-gray-900 rounded-2xl p-2 border border-gray-800">
                                <div className="flex flex-col md:flex-row">
                                    <div className="flex-grow p-3 md:p-4">
                                        <div className="flex items-center">
                                            <div className="flex-grow">
                                                <div className="flex items-center mb-1">
                                                    <i className="fas fa-search text-gray-500 mr-2 text-sm"></i>
                                                    <label className="text-sm font-medium text-gray-300">Search jobs, roles, or skills</label>
                                                </div>
                                                <input 
                                                    type="text" 
                                                    id="job-search"
                                                    className="w-full text-white text-lg p-2 bg-transparent focus:outline-none placeholder-gray-500"
                                                    placeholder="e.g. 'Agronomist', 'Accountant', 'Clinical Research Associate'"
                                                    value={searchData.jobQuery}
                                                    onChange={(e) => setSearchData(prev => ({...prev, jobQuery: e.target.value}))}
                                                    onKeyPress={handleKeyPress}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="border-t md:border-t-0 md:border-l border-gray-800 p-3 md:p-4">
                                        <div className="flex items-center">
                                            <div className="flex-grow">
                                                <div className="flex items-center mb-1">
                                                    <i className="fas fa-map-marker-alt text-gray-500 mr-2 text-sm"></i>
                                                    <label className="text-sm font-medium text-gray-300">Location</label>
                                                </div>
                                                <input 
                                                    type="text" 
                                                    id="location-search"
                                                    className="w-full text-white p-2 bg-transparent focus:outline-none placeholder-gray-500"
                                                    placeholder="City, country, or 'Remote'"
                                                    value={searchData.location}
                                                    onChange={(e) => setSearchData(prev => ({...prev, location: e.target.value}))}
                                                    onKeyPress={handleKeyPress}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-3 md:p-4 flex items-center">
                                        <button 
                                            id="search-button"
                                            className="w-full md:w-auto bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] text-white font-semibold px-6 md:px-8 py-4 rounded-xl flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/20 disabled:opacity-70"
                                            onClick={handleSearch}
                                            disabled={isSearching}
                                        >
                                            {isSearching ? (
                                                <>
                                                    <i className="fas fa-spinner fa-spin mr-3"></i>
                                                    <span className="text-lg">Searching...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-search mr-3"></i>
                                                    <span className="text-lg">Search</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Quick Search Tags */}
                            <div className="mt-6 text-center">
                                <p className="text-gray-400 text-sm mb-3">Popular searches:</p>
                                <div className="flex flex-wrap justify-center gap-2">
                                    {['Remote Jobs', 'Financial Analyst', 'Farm Operations Manager', 'Data Science', 'Product Manager', 'Medical Lab Technician'].map((tag, index) => (
                                        <button 
                                            key={index}
                                            className="text-sm px-4 py-2 rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition"
                                            onClick={() => handleTagClick(tag)}
                                        >
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Trust Indicators */}
                            <div className="mt-12 flex flex-wrap justify-center items-center gap-6 text-gray-400 text-sm md:text-base transform -translate-y-4 fade-in-up delay-2">
                                <div className="flex items-center">
                                    <i className="fas fa-check-circle text-[#06D6A0] mr-2"></i>
                                    <span>Sector-Focused Jobs in Healthcare, Finance & Agritech</span>
                                </div>
                                <div className="hidden md:block text-gray-600">•</div>
                                <div className="flex items-center">
                                    <i className="fas fa-check-circle text-[#06D6A0] mr-2"></i>
                                    <span>Smart Skill-Based Personalised Matching</span>
                                </div>
                                <div className="hidden md:block text-gray-600">•</div>
                                <div className="flex items-center">
                                    <i className="fas fa-check-circle text-[#06D6A0] mr-2"></i>
                                    <span>Free for Job Seekers</span>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>            
        </div>        
    );
};



export default Home;